import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Plane, Bus, MapPin, Sparkles, ShieldCheck, Clock, DollarSign, Star, ArrowRight, Car, Route, Briefcase, FileText, Phone, MessageSquare, Compass } from "lucide-react";
import { ReviewsSection } from "@/components/ReviewsSection";

const HERO = "https://static.prod-images.emergentagent.com/jobs/b894d560-1edd-4384-a4a7-bb5a6fd2cfd6/images/c61869ab645b8d2d81ffa6fde7931362b10572814f451b6400ee03776cb37782.png";
const SKYLINE = "https://images.unsplash.com/photo-1755531196237-0cb37838cb05?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxoYWxpZmF4JTIwbm92YSUyMHNjb3RpYSUyMHNreWxpbmV8ZW58MHx8fHwxNzg0NDI2ODQ0fDA&ixlib=rb-4.1.0&q=85";

const services = [
  { icon: Plane, title: "Private Airport Car", desc: "Direct door-to-door airport transfers in a private vehicle. Flat fares from $55.", tag: "From $55" },
  { icon: Bus, title: "Shared Airport Shuttle", desc: "Share the ride and save — a simple flat rate of $45 per passenger.", tag: "$45 per person" },
  { icon: Car, title: "Private Group Airport Shuttle", desc: "The whole group from one address — per-person pricing that drops as your group grows.", tag: "From $15 pp" },
  { icon: MapPin, title: "Tours, Trips & Events", desc: "Guided tours, long-distance trips, weddings & charters — request a custom quote.", tag: "Get a quote" },
];

const features = [
  { icon: ShieldCheck, title: "Licensed & Insured", desc: "Friendly, fully licensed drivers who know every road in Nova Scotia." },
  { icon: DollarSign, title: "Upfront Pricing", desc: "See your price before you book — no surge, no hidden fees, ever." },
  { icon: Clock, title: "On Time, Every Time", desc: "We track your flight and arrive on time, so you never have to wait." },
];

const fade = (d = 0) => ({
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, delay: d, ease: "easeOut" },
});

export default function Home() {
  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-[90vh] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO} alt="Luxury car" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-navy via-navy/85 to-navy/40" />
        </div>
        <div className="relative z-10 max-w-[1720px] mx-auto px-5 sm:px-10 w-full py-20 sm:py-24">
          <motion.div className="max-w-2xl" {...fade(0)}>
            <span className="inline-block text-[11px] sm:text-xs uppercase tracking-[0.25em] sm:tracking-[0.3em] text-gold border border-gold/40 rounded-full px-4 py-1.5 mb-6 sm:mb-7">
              Halifax • Nova Scotia • Canada
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold text-white tracking-tighter leading-[1.08] sm:leading-[1.05]">
              Choose your destination, cross the bridge, and book your ride with <span className="text-gold">Bridge Taxi</span>.
            </h1>
            <p className="mt-5 sm:mt-7 text-base sm:text-lg text-slate-300 leading-relaxed max-w-xl">
              Airport transfers, shared shuttles and private rides across Nova Scotia — premium comfort,
              friendly drivers and honest, upfront pricing every time.
            </p>
            <div className="mt-8 sm:mt-10 flex flex-col sm:flex-row gap-3 sm:gap-4">
              <Link to="/book" data-testid="hero-book-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 font-semibold text-navy transition-transform duration-200 hover:-translate-y-1 hover:bg-gold-light">
                Book Your Ride <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/services" data-testid="hero-pricing-btn" className="inline-flex items-center justify-center gap-2 rounded-full border border-gold/40 px-8 py-4 font-semibold text-white transition-colors duration-200 hover:border-gold hover:text-gold">
                View Pricing
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="max-w-[1720px] mx-auto px-5 sm:px-10 py-16 sm:py-24">
        <motion.div {...fade(0)} className="max-w-2xl mb-14">
          <span className="text-sm uppercase tracking-[0.2em] text-gold">What we offer</span>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight">Services built around your journey</h2>
        </motion.div>
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {services.map((s, i) => (
            <motion.div key={s.title} {...fade(i * 0.08)}
              className="group relative rounded-sm bg-navy-light border border-gold/20 p-8 transition-transform duration-200 hover:-translate-y-1.5 hover:border-gold/50">
              <div className="grid place-items-center h-14 w-14 rounded-sm bg-gold/10 text-gold mb-6 transition-colors duration-200 group-hover:bg-gold group-hover:text-navy">
                <s.icon className="h-7 w-7" />
              </div>
              <div className="text-xs uppercase tracking-[0.15em] text-gold mb-2">{s.tag}</div>
              <h3 className="font-heading text-xl font-bold text-white mb-3">{s.title}</h3>
              <p className="text-slate-400 text-sm leading-relaxed">{s.desc}</p>
            </motion.div>
          ))}
        </div>
        <div className="mt-10">
          <Link to="/services" className="inline-flex items-center gap-2 text-gold font-semibold hover:gap-3 transition-all duration-200" data-testid="home-all-services">
            See full pricing table <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* READY-MADE TOURS */}
      <section className="max-w-[1720px] mx-auto px-5 sm:px-10 pt-16 sm:pt-24 pb-4">
        <div className="grid gap-8 lg:grid-cols-12 items-center">
          <motion.div {...fade(0)} className="lg:col-span-5 relative rounded-sm overflow-hidden border border-gold/20 min-h-[320px]">
            <img src="https://images.unsplash.com/photo-1669589749736-b83e8b6add35?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200" alt="Peggy's Cove lighthouse, Nova Scotia" className="absolute inset-0 h-full w-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-navy via-navy/30 to-transparent" />
          </motion.div>
          <motion.div {...fade(0.1)} className="lg:col-span-7">
            <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-gold"><Compass className="h-3.5 w-3.5" /> Ready-Made Tours</span>
            <h2 className="mt-4 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-[1.1]">Discover Nova Scotia — book a tour in one click</h2>
            <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed">
              Sit back and take in the coastline. Our guided Charter Touring Service visits the province's most-loved
              historic and scenic spots — Peggy's Cove, Lunenburg, the Annapolis Valley, the Bay of Fundy and more —
              with a friendly local driver who knows every stop.
            </p>
            <div className="mt-6 flex flex-wrap gap-2.5">
              {["Halifax City & Peggy's Cove", "Lunenburg & the South Shore", "Annapolis Valley & Bay of Fundy"].map((t) => (
                <span key={t} className="inline-flex items-center gap-2 rounded-full border border-gold/25 bg-navy-light px-4 py-2 text-sm text-slate-200">
                  <MapPin className="h-3.5 w-3.5 text-gold" /> {t}
                </span>
              ))}
            </div>
            <div className="mt-6 rounded-sm border border-gold/20 bg-navy-light px-5 py-4">
              <div className="flex items-center gap-2 text-gold font-semibold"><Clock className="h-4 w-4" /> Hourly touring rates</div>
              <p className="mt-1.5 text-slate-300 text-sm">
                <span className="text-white font-semibold">$65/hour</span> for 1–3 passengers · <span className="text-white font-semibold">$75/hour</span> for 4–6 passengers — <span className="text-gold">HST included</span>. 3-hour minimum.
              </p>
            </div>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/book?service=tour" data-testid="home-book-tour" className="inline-flex items-center gap-2 rounded-full bg-gold px-8 py-4 font-semibold text-navy transition-transform duration-200 hover:-translate-y-1 hover:bg-gold-light">
                Book a Tour <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/explore" data-testid="home-tours-explore" className="inline-flex items-center gap-2 rounded-full border border-gold/40 px-8 py-4 font-semibold text-white transition-colors duration-200 hover:border-gold hover:text-gold">
                Explore our tours <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </motion.div>
        </div>
      </section>

      {/* INQUIRY NOTICE — request a personalized quote */}
      <section className="max-w-[1720px] mx-auto px-5 sm:px-10 pt-4 pb-12 sm:pb-16">
        <motion.div {...fade(0)} className="relative overflow-hidden rounded-sm border border-gold/30 bg-navy-light grain" data-testid="home-inquiry-notice">
          <div className="absolute inset-0 bg-gradient-to-br from-gold/10 via-transparent to-transparent" />
          <div className="absolute -right-10 -top-10 h-52 w-52 rounded-full bg-gold/5 blur-2xl" />
          <div className="relative z-10 p-8 sm:p-12 lg:p-16">
            <div className="max-w-3xl">
              <span className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-gold border border-gold/40 rounded-full px-4 py-1.5">
                <FileText className="h-3.5 w-3.5" /> Personalized Quotes
              </span>
              <h2 className="mt-6 font-heading text-3xl sm:text-4xl lg:text-5xl font-bold text-white tracking-tight leading-[1.1]">
                Tell us your journey — we'll tailor the price to you.
              </h2>
              <p className="mt-5 text-base sm:text-lg text-slate-300 leading-relaxed">
                From a single airport run to a full-day regional tour or flexible hourly hire, every trip is
                a little different. Share your plans and our team will prepare a clear, personalized quote —
                with upfront, honest pricing and no obligation.
              </p>
            </div>

            <div className="mt-9 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[
                { icon: Route, t: "Regional Long-Distance Transit", d: "Across Nova Scotia, New Brunswick & PEI.", link: "/quote?service=Long%20Distance%20Trip" },
                { icon: Plane, t: "Airport Transfers — Halifax Stanfield", d: "Reliable, on-time arrivals & departures.", link: "/book" },
                { icon: Sparkles, t: "Private Events", d: "Weddings, celebrations & special occasions.", link: "/quote?service=Special%20Event" },
                { icon: MapPin, t: "Charter Touring Service", d: "Historic and scenic site tours across Nova Scotia.", link: "/quote?service=Charter%20Touring%20Service" },
                { icon: Briefcase, t: "Corporate Accounts", d: "Priority service & billing for your company.", link: "/quote?service=Corporate%20Account" },
                { icon: Clock, t: "Hourly Service", d: "Book by the hour for events, errands & charters.", link: "/quote?service=Hourly%20Service" },
                { icon: Bus, t: "Group Charters", d: "Comfortable transport for larger parties.", link: "/quote?service=Group%20Charter" },
              ].map((s, i) => (
                <motion.div key={s.t} {...fade(0.05 + i * 0.05)}>
                  <Link to={s.link} data-testid={`home-service-card-${i}`} className="group flex h-full items-start gap-4 rounded-sm border border-gold/15 bg-navy p-5 transition-colors duration-200 hover:border-gold/45 hover:-translate-y-0.5">
                    <span className="grid place-items-center h-11 w-11 shrink-0 rounded-sm bg-gold/10 text-gold transition-colors duration-200 group-hover:bg-gold group-hover:text-navy">
                      <s.icon className="h-5 w-5" />
                    </span>
                    <div>
                      <h3 className="font-heading text-base font-bold text-white leading-snug">{s.t}</h3>
                      <p className="text-slate-400 text-sm mt-1 leading-relaxed">{s.d}</p>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>

            <div className="mt-10 flex flex-col sm:flex-row items-stretch sm:items-center gap-3 sm:gap-4">
              <Link to="/quote" data-testid="inquiry-quote-btn" className="inline-flex items-center justify-center gap-2 rounded-full bg-gold px-8 py-4 font-semibold text-navy transition-transform duration-200 hover:-translate-y-1 hover:bg-gold-light">
                <FileText className="h-4 w-4" /> Request a personalized quote
              </Link>
              <a href="https://wa.me/19024407855" target="_blank" rel="noopener noreferrer" data-testid="inquiry-whatsapp-btn" className="inline-flex items-center justify-center gap-2 rounded-full border border-gold/40 px-8 py-4 font-semibold text-white transition-colors duration-200 hover:border-gold hover:text-gold">
                <Phone className="h-4 w-4 text-gold" /> Message us on WhatsApp
              </a>
              <a href="sms:+19024016212" data-testid="inquiry-sms-btn" className="inline-flex items-center justify-center gap-2 rounded-full border border-gold/40 px-8 py-4 font-semibold text-white transition-colors duration-200 hover:border-gold hover:text-gold">
                <MessageSquare className="h-4 w-4 text-gold" /> Text us: 902-401-6212
              </a>
              <span className="text-sm text-slate-400 sm:ml-2 self-center">Fast replies · No obligation · Upfront pricing</span>
            </div>
          </div>
        </motion.div>
      </section>

      {/* WHY US */}
      <section className="bg-navy-light border-y border-gold/10 relative grain">
        <div className="max-w-[1720px] mx-auto px-5 sm:px-10 py-16 sm:py-24 grid gap-10 lg:gap-16 lg:grid-cols-2 items-center relative z-10">
          <div>
            <span className="text-sm uppercase tracking-[0.2em] text-gold">Why Bridge Taxi</span>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight mb-10">
              The dependable choice in Nova Scotia
            </h2>
            <div className="space-y-8">
              {features.map((f, i) => (
                <motion.div key={f.title} {...fade(i * 0.1)} className="flex gap-5">
                  <div className="grid place-items-center h-12 w-12 shrink-0 rounded-sm bg-gold/10 text-gold">
                    <f.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h3 className="font-heading text-lg font-bold text-white">{f.title}</h3>
                    <p className="text-slate-400 mt-1 leading-relaxed">{f.desc}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap items-center gap-x-6 gap-y-3 border-t border-gold/15 pt-6" data-testid="home-trust-badges">
              <div className="flex items-center gap-1.5 text-gold">
                {[...Array(5)].map((_, i) => <Star key={i} className="h-4 w-4 fill-gold" />)}
              </div>
              <span className="text-sm text-slate-300">Trusted by hundreds of Halifax travellers</span>
              <span className="text-sm text-gold/80 uppercase tracking-[0.15em]">Est. 2022 · Professional Driving Services</span>
            </div>
          </div>
          <motion.div {...fade(0.1)} className="relative">
            <img src={SKYLINE} alt="Halifax skyline" className="rounded-sm border border-gold/20 max-h-[80vh] w-full object-cover" />
            <div className="absolute -bottom-6 -left-6 bg-gold text-navy rounded-sm px-8 py-6 hidden sm:block">
              <div className="font-heading text-3xl font-bold">24/7</div>
              <div className="text-xs uppercase tracking-[0.15em] font-semibold">Always Available</div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* SERVICE AREA MAP */}
      <section className="max-w-[1720px] mx-auto px-5 sm:px-10 py-16">
        <div className="grid gap-8 lg:grid-cols-5 items-center">
          <motion.div {...fade(0)} className="lg:col-span-2">
            <span className="text-sm uppercase tracking-[0.2em] text-gold">Where we serve</span>
            <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold text-white tracking-tight">Based in Halifax, serving the Maritimes</h2>
            <p className="mt-4 text-slate-400 leading-relaxed">
              From Halifax Stanfield Airport to Peggy's Cove, Lunenburg, Cape Breton and beyond — and long-distance
              trips across Nova Scotia, New Brunswick and PEI. Available 24/7.
            </p>
            <Link to="/book" data-testid="home-book-cta"
              className="mt-6 inline-flex items-center gap-2 rounded-full border border-gold/40 px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:border-gold hover:text-gold">
              <MapPin className="h-4 w-4 text-gold" /> Book a ride
            </Link>
          </motion.div>
          <motion.div {...fade(0.1)} className="lg:col-span-3 rounded-sm overflow-hidden border border-gold/20" data-testid="home-map">
            <iframe
              title="Bridge Taxi service area — Halifax, Nova Scotia"
              src="https://maps.google.com/maps?q=Halifax%2C%20Nova%20Scotia%2C%20Canada&t=&z=10&ie=UTF8&iwloc=&output=embed"
              className="w-full h-80"
              style={{ border: 0, filter: "grayscale(0.2) contrast(1.05)" }}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>
        </div>
      </section>

      {/* REVIEWS */}
      <ReviewsSection limit={6} />

      {/* CTA */}
      <section className="max-w-[1720px] mx-auto px-5 sm:px-10 py-16 sm:py-24">
        <motion.div {...fade(0)} className="rounded-sm bg-gradient-to-br from-gold to-gold-light p-10 sm:p-16 text-navy overflow-hidden relative">
          <Car className="absolute -right-6 -bottom-6 h-56 w-56 opacity-10" />
          <div className="relative max-w-2xl">
            <h2 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Ready when you are.</h2>
            <p className="mt-4 text-navy/80 text-lg">Book your airport ride in under a minute — pay online now, or simply pay your driver on arrival.</p>
            <Link to="/book" data-testid="cta-book-btn" className="mt-8 inline-flex items-center gap-2 rounded-full bg-navy px-8 py-4 font-semibold text-white transition-transform duration-200 hover:-translate-y-1">
              Book Now <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </motion.div>
      </section>
    </div>
  );
}
